/**
 * useRequests.ts — React Query hooks for all BlinkRequest data operations.
 *
 * Query key factory (requestKeys) ensures consistent cache scoping:
 *   ['requests', 'list', filters]   → paginated reviewer/PM list
 *   ['requests', 'mine', filters]   → requestor-scoped list
 *   ['requests', 'detail', id]      → single request detail
 *
 * After any mutation that changes a request, we invalidate the relevant
 * list caches so UI counts and tables stay in sync without a manual refresh.
 * The detail cache is also updated/invalidated so open modals reflect the
 * latest state immediately.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  requestsApi,
  workflowApi,
  respondApi,
  threadApi,
  ticketsApi,
  assignmentApi,
} from "@/lib/api";
import type {
  ApprovePayload,
  ApproveResult,
  BlinkRequest,
  ClarifyPayload,
  ClassificationPayload,
  RequestTicket,
  RequestTickets,
  Reviewer,
  TicketCreatePayload,
  Message,
  RejectPayload,
  RequestCreate,
  RequestFilters,
  RequestUpdate,
  RespondPayload,
  StatusUpdatePayload,
  TimelineEvent,
} from "@/lib/types";
import { threadKeys } from "./useThread";
import { isLive, POLL_MS } from "@/lib/liveUpdates";

// ── Query key factory ─────────────────────────────────────────────────────────
// Structured keys let us invalidate by prefix (e.g. all list queries) without
// needing to track exact filter objects at call sites.

export const requestKeys = {
  all: ["requests"] as const,
  lists: () => [...requestKeys.all, "list"] as const,
  list: (filters: RequestFilters) => [...requestKeys.lists(), filters] as const,
  mineLists: () => [...requestKeys.all, "mine"] as const,
  mineList: (filters: RequestFilters) =>
    [...requestKeys.mineLists(), filters] as const,
  details: () => [...requestKeys.all, "detail"] as const,
  detail: (id: string) => [...requestKeys.details(), id] as const,
  timelines: () => [...requestKeys.all, "timeline"] as const,
  timeline: (id: string) => [...requestKeys.timelines(), id] as const,
};

// ── Queries ───────────────────────────────────────────────────────────────────

/** Reviewer/PM view — all requests, filtered + paginated. */
export function useRequests(
  filters: RequestFilters = {},
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: requestKeys.list(filters),
    queryFn: () => requestsApi.list(filters),
    ...options,
  });
}

/** Requestor view — only requests submitted by the current user. */
export function useMyRequests(
  filters: RequestFilters = {},
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: requestKeys.mineList(filters),
    queryFn: () => requestsApi.listMine(filters),
    ...options,
  });
}

const TERMINAL_STATUSES = new Set([
  "Completed",
  "Closed",
  "Cancelled",
  "Rejected",
]);

/** Single request detail — disabled until `id` is available (avoids 404 on mount). */
export function useRequest(id: string) {
  return useQuery({
    queryKey: requestKeys.detail(id),
    queryFn: () => requestsApi.get(id),
    enabled: !!id,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Fast-poll while waiting for the Jira ticket key to appear (Celery task lag).
      // A request approved without a ticket isn't waiting for one.
      if (
        data?.status === "Approved" &&
        !data?.jira_ticket_key &&
        data?.jira_ticket_pending
      )
        return 2_000;
      // Active request: changes are pushed over the thread's live connection;
      // without one, poll every 5 s so Jira/JSM-driven changes still appear.
      if (data && !TERMINAL_STATUSES.has(data.status))
        return isLive(id) ? POLL_MS.live : POLL_MS.fallback;
      // Terminal — no further changes expected.
      return false;
    },
  });
}

export function useSimilarRequests(id: string | null) {
  return useQuery({
    queryKey: [...requestKeys.detail(id ?? ""), "similar"],
    queryFn: () => requestsApi.getSimilar(id!),
    enabled: !!id,
  });
}

export function useRequestTimeline(id: string | null) {
  return useQuery({
    queryKey: requestKeys.timeline(id ?? ""),
    queryFn: () => (id ? requestsApi.getTimeline(id) : Promise.resolve([])),
    enabled: !!id,
    staleTime: 60_000,
    refetchInterval: () =>
      id && isLive(id) ? POLL_MS.live : POLL_MS.timelineFallback,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCreateRequest() {
  const queryClient = useQueryClient();
  return useMutation<BlinkRequest, Error, RequestCreate>({
    mutationFn: requestsApi.create,
    onSuccess: () => {
      // New request added — refresh all list views (dashboard, mine)
      void queryClient.invalidateQueries({ queryKey: requestKeys.lists() });
    },
  });
}

export function useUpdateRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<BlinkRequest, Error, RequestUpdate>({
    mutationFn: (payload) => requestsApi.update(requestId, payload),
    onSuccess: (req) => {
      // Directly set the detail cache with the response so the UI reflects
      // changes immediately without waiting for an extra refetch.
      queryClient.setQueryData(requestKeys.detail(requestId), req);
      void queryClient.invalidateQueries({ queryKey: requestKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: requestKeys.mineLists() });
    },
  });
}

export function useUpdateStatus(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    { id: string; status: string },
    Error,
    StatusUpdatePayload
  >({
    mutationFn: (payload) => workflowApi.updateStatus(requestId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(requestId),
      });
      void queryClient.invalidateQueries({ queryKey: requestKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.timeline(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

export function useApproveRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<ApproveResult, Error, ApprovePayload>({
    mutationFn: (payload) => workflowApi.approve(requestId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketKeys.list(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(requestId),
      });
      void queryClient.invalidateQueries({ queryKey: requestKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: requestKeys.mineLists() });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.timeline(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

/** Uses /reject endpoint (not /status) so the backend fires the rejection email. */
export function useRejectRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ id: string; status: string }, Error, RejectPayload>({
    mutationFn: (payload) => workflowApi.reject(requestId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(requestId),
      });
      void queryClient.invalidateQueries({ queryKey: requestKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: requestKeys.mineLists() });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.timeline(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

export function useUpdateClassification(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    { id: string; request_type: string; pod: string },
    Error,
    ClassificationPayload
  >({
    mutationFn: (payload) =>
      workflowApi.updateClassification(requestId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(requestId),
      });
      void queryClient.invalidateQueries({ queryKey: requestKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: requestKeys.mineLists() });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.timeline(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

export const ticketKeys = {
  list: (requestId: string) => ["requests", "tickets", requestId] as const,
};

/** Jira tickets raised from a request — reviewers only. */
export function useRequestTickets(requestId: string, enabled = true) {
  return useQuery<RequestTickets>({
    queryKey: ticketKeys.list(requestId),
    queryFn: () => ticketsApi.list(requestId),
    enabled: !!requestId && enabled,
  });
}

export function useCreateTicket(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<RequestTicket, Error, TicketCreatePayload>({
    mutationFn: (payload) => ticketsApi.create(requestId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketKeys.list(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.timeline(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

export function useLinkTicket(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<RequestTicket, Error, string>({
    mutationFn: (issue) => ticketsApi.link(requestId, issue),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketKeys.list(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

export function useUnlinkTicket(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ issue_key: string; unlinked: boolean }, Error, string>({
    mutationFn: (issueKey) => ticketsApi.unlink(requestId, issueKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketKeys.list(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

/** Reviewers a request can be assigned to. */
export function useReviewers(enabled = true) {
  return useQuery<Reviewer[]>({
    queryKey: ["reviewers"],
    queryFn: () => assignmentApi.reviewers(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useAssignRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    { id: string; claimed_by: string; claimed_by_email: string },
    Error,
    string
  >({
    mutationFn: (email) => assignmentApi.assign(requestId, email),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(requestId),
      });
      void queryClient.invalidateQueries({ queryKey: requestKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.timeline(requestId),
      });
    },
  });
}

export function useCancelRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ id: string; status: string }, Error>({
    mutationFn: () => workflowApi.cancel(requestId),
    onSuccess: (data) => {
      const detailKey = requestKeys.detail(requestId);
      queryClient.setQueryData(
        detailKey,
        (oldData: BlinkRequest | undefined) => {
          if (!oldData) return oldData;
          return { ...oldData, status: data.status as any };
        },
      );

      void queryClient.invalidateQueries({ queryKey: requestKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: requestKeys.mineLists() });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.timeline(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

export function useResubmitRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<BlinkRequest, Error, RequestUpdate>({
    mutationFn: (payload) => requestsApi.resubmit(requestId, payload),
    onSuccess: (req) => {
      queryClient.setQueryData(requestKeys.detail(requestId), req);
      void queryClient.invalidateQueries({ queryKey: requestKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: requestKeys.mineLists() });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.timeline(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

/**
 * Used on the public /respond/:id page — no reviewer auth required.
 * Only invalidates the detail cache; the requestor has no list view.
 */
export function useRespondToRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ id: string; status: string }, Error, RespondPayload>({
    mutationFn: (payload) => respondApi.respond(requestId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(requestId),
      });
    },
  });
}

/** Reviewer sends a clarification request, transitioning status to AwaitingInfo. */
export function useSendClarification(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<Message, Error, ClarifyPayload>({
    mutationFn: (payload) => threadApi.clarify(requestId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.timeline(requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

/** Get escalation metrics: requests in AwaitingInfo >7 days. */
export function useEscalationSummary() {
  return useQuery({
    queryKey: ["escalations", "summary"] as const,
    queryFn: () => requestsApi.getEscalationSummary(),
    refetchInterval: 60 * 1000, // Refresh every minute
    staleTime: 30 * 1000, // Data fresh for 30 seconds
  });
}
